import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Package,
  Search,
  Filter,
  Truck,
  CheckCircle2,
  XCircle,
  RefreshCcw,
  Printer,
  Send,
  Undo2,
  Ban,
  IndianRupee,
  Trash2,
  Hash,
} from "lucide-react";
import {
  listOrders,
  getOrder,
  updateOrder,
  fetchOrderStats,
  adminShipOrder,
  adminCancelShipment,
  adminCreateReturn,
  adminRefreshTracking,
  adminRefund,
  bulkDeleteOrders,
  renumberAllOrders,
} from "../api";
import { printInvoice } from "../lib/printInvoice";
import { PageHeader } from "./Dashboard";
import RefreshButton from "../components/RefreshButton";

const STATUSES = ["pending", "confirmed", "packed", "shipped", "delivered", "cancelled", "refunded"];

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-[#c79a3a]/15 text-[#c79a3a] border-[#c79a3a]/40",
    confirmed: "bg-[#5f8fbe]/15 text-[#8cb4d9] border-[#8cb4d9]/40",
    packed: "bg-[#5f8fbe]/15 text-[#8cb4d9] border-[#8cb4d9]/40",
    shipped: "bg-[#3b6e4a]/20 text-[#6ba67a] border-[#6ba67a]/40",
    delivered: "bg-[#3b6e4a]/30 text-[#8ccf9a] border-[#8ccf9a]/40",
    cancelled: "bg-[#c97070]/20 text-[#e69a9a] border-[#e69a9a]/40",
    refunded: "bg-[#7a7a7a]/20 text-[#9a9787] border-[#9a9787]/40",
  };
  return (
    <span className={`inline-block border px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase ${map[status] || ""}`} data-testid={`order-status-${status}`}>
      {status}
    </span>
  );
};

export function OrdersList() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const reload = () => {
    setLoading(true);
    setSelected(new Set());
    listOrders({ status: statusFilter, search })
      .then(setOrders)
      .finally(() => setLoading(false));
    fetchOrderStats().then(setStats).catch(() => {});
  };
  useEffect(() => {
    const t = setTimeout(reload, search ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [statusFilter, search]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === orders.length) setSelected(new Set());
    else setSelected(new Set(orders.map((o) => o.id)));
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Permanently delete ${selected.size} order${selected.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await bulkDeleteOrders(Array.from(selected));
      toast.success(`${selected.size} order${selected.size === 1 ? "" : "s"} deleted`);
      reload();
    } catch {
      toast.error("Could not delete orders");
    } finally {
      setBulkDeleting(false);
    }
  };

  const renumberAll = async () => {
    if (!window.confirm("Renumber every order chronologically as ASV<YEAR>001, 002, 003…? Existing order numbers will be REWRITTEN. Any invoices already emailed to customers will keep the old number — new invoices printed from this admin will show the new one.")) return;
    try {
      const res = await renumberAllOrders();
      toast.success(`${res.orders_updated} order${res.orders_updated === 1 ? "" : "s"} renumbered`);
      reload();
    } catch {
      toast.error("Renumber failed");
    }
  };

  const statCards = [
    { label: "Total orders", value: stats?.total_orders, icon: Package },
    { label: "Pending", value: stats?.pending_orders, icon: RefreshCcw, accent: "text-[#c79a3a]" },
    { label: "Shipped", value: stats?.shipped_orders, icon: Truck, accent: "text-[#8cb4d9]" },
    { label: "Delivered", value: stats?.delivered_orders, icon: CheckCircle2, accent: "text-[#8ccf9a]" },
    { label: "Revenue", value: stats ? fmt(stats.revenue) : "—", icon: CheckCircle2 },
  ];

  return (
    <div data-testid="admin-orders-list">
      <PageHeader eyebrow="Fulfillment" title="Orders" sub={`${orders.length} matching · managed live`} />
      <div className="p-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {statCards.map((s) => (
            <div key={s.label} className="bg-[#161617] border border-[#2a2a2d] p-5">
              <s.icon size={16} className={s.accent || "text-[#c3ba9e]/70"} />
              <p className="font-serif-display text-3xl text-[#e9e6db] mt-4 leading-none">
                {s.value == null ? "—" : s.value}
              </p>
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e] mt-2">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="bg-[#161617] border border-[#2a2a2d]">
          <div className="p-4 flex flex-wrap items-center gap-3 border-b border-[#2a2a2d]">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5e5d54]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by order #, name, email, phone"
                className="w-full bg-[#0d0d0e] border border-[#2a2a2d] pl-9 pr-3 py-2.5 text-[13px] text-[#e9e6db] focus:border-[#c3ba9e] outline-none"
                data-testid="admin-orders-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-[#5e5d54]" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-[#0d0d0e] border border-[#2a2a2d] px-3 py-2.5 text-[13px] text-[#e9e6db] focus:border-[#c3ba9e] outline-none"
                data-testid="admin-orders-filter"
              >
                <option value="all">All statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <RefreshButton onClick={reload} loading={loading} testid="admin-orders-refresh" />
            <button
              onClick={renumberAll}
              className="flex items-center gap-2 border border-[#2a2a2d] text-[#9a9787] px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#1f1f21]"
              data-testid="admin-orders-renumber"
              title="Renumber all orders chronologically as ASV<YEAR>001+"
            >
              <Hash size={13} /> Renumber
            </button>
            {selected.size > 0 && (
              <button
                onClick={bulkDelete}
                disabled={bulkDeleting}
                className="flex items-center gap-2 bg-[#c97070]/20 text-[#e69a9a] border border-[#e69a9a]/40 px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#c97070]/30 disabled:opacity-60"
                data-testid="admin-orders-bulk-delete"
              >
                <Trash2 size={13} /> Delete {selected.size}
              </button>
            )}
          </div>

          <div className="grid grid-cols-[40px_1.2fr_1.4fr_1fr_1fr_1fr_0.7fr] gap-4 px-6 py-3 border-b border-[#2a2a2d] text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e] items-center">
            <input
              type="checkbox"
              checked={orders.length > 0 && selected.size === orders.length}
              onChange={toggleAll}
              className="w-4 h-4 cursor-pointer accent-[#c3ba9e]"
              data-testid="admin-orders-select-all"
              aria-label="Select all orders"
            />
            <span>Order</span>
            <span>Customer</span>
            <span>Total</span>
            <span>Payment</span>
            <span>Status</span>
            <span></span>
          </div>

          {loading ? (
            <p className="p-10 text-center text-[#8c8a7e] text-sm">Loading…</p>
          ) : orders.length === 0 ? (
            <p className="p-10 text-center text-[#8c8a7e] text-sm">No orders yet.</p>
          ) : (
            orders.map((o) => (
              <div
                key={o.id}
                onClick={() => navigate(`/admin/orders/${o.id}`)}
                className={`grid grid-cols-[40px_1.2fr_1.4fr_1fr_1fr_1fr_0.7fr] gap-4 px-6 py-4 border-b border-[#2a2a2d] items-center cursor-pointer transition-colors ${
                  selected.has(o.id) ? "bg-[#1a1a1c]" : "hover:bg-[#1a1a1c]"
                }`}
                data-testid={`admin-order-row-${o.order_number}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(o.id)}
                  onChange={() => toggleOne(o.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 cursor-pointer accent-[#c3ba9e]"
                  data-testid={`admin-order-select-${o.order_number}`}
                  aria-label={`Select order ${o.order_number}`}
                />
                <div>
                  <p className="text-[#e9e6db] text-sm">{o.order_number}</p>
                  <p className="text-[11px] text-[#5e5d54] mt-0.5">
                    {new Date(o.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })} · {o.items.length} items
                  </p>
                </div>
                <div>
                  <p className="text-[#e9e6db] text-sm">{o.shipping.full_name}</p>
                  <p className="text-[11px] text-[#5e5d54] mt-0.5">{o.shipping.email}</p>
                </div>
                <span className="text-[#e9e6db] text-sm">{fmt(o.totals.total)}</span>
                <span className="text-[12px] text-[#9a9787] uppercase tracking-wider">
                  {o.payment_method} · {o.payment_status}
                </span>
                <StatusBadge status={o.status} />
                <span className="text-right text-[11px] tracking-[0.22em] uppercase text-[#8c8a7e]">View →</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => getOrder(id).then(setOrder);
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const patch = async (update) => {
    setSaving(true);
    try {
      const fresh = await updateOrder(id, update);
      setOrder(fresh);
      toast.success("Order updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const runShipAction = async (label, fn) => {
    if (saving) return;
    setSaving(true);
    try {
      const result = await fn();
      const fresh = result?.order || result;
      if (fresh && fresh.id) setOrder(fresh);
      else await load();
      toast.success(`${label} successful`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `${label} failed`);
    } finally {
      setSaving(false);
    }
  };

  const ship = () => runShipAction("Ship via Shiprocket", () => adminShipOrder(id));
  const cancelShipment = () => {
    if (!window.confirm("Cancel this shipment in Shiprocket? This cannot be undone.")) return;
    return runShipAction("Cancel shipment", () => adminCancelShipment(id));
  };
  const createReturn = () => {
    if (!window.confirm("Create a return pickup from the customer's address?")) return;
    return runShipAction("Create return", () => adminCreateReturn(id));
  };
  const refreshTracking = () => runShipAction("Tracking refresh", () => adminRefreshTracking(id));
  const refund = () => {
    if (!window.confirm("Issue a FULL refund via Razorpay? This cannot be reversed.")) return;
    return runShipAction("Refund", () => adminRefund(id));
  };

  if (!order) {
    return (
      <div>
        <PageHeader eyebrow="Order" title="Loading…" />
      </div>
    );
  }

  return (
    <div data-testid="admin-order-detail">
      <PageHeader eyebrow={`Order · ${order.order_number}`} title={`₹${order.totals.total.toLocaleString("en-IN")}`} sub={new Date(order.created_at).toLocaleString("en-IN")} />
      <div className="p-10 max-w-5xl">
        <Link to="/admin/orders" className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#8c8a7e] hover:text-[#c3ba9e] mb-8">
          <ArrowLeft size={14} /> Back to orders
        </Link>

        <div className="grid lg:grid-cols-[1fr_340px] gap-8">
          <div className="space-y-6">
            {/* Status bar */}
            <div className="bg-[#161617] border border-[#2a2a2d] p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">Current status</p>
                <StatusBadge status={order.status} />
              </div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => patch({ status: s })}
                    disabled={saving || order.status === s}
                    className={`px-3 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
                      order.status === s
                        ? "bg-[#c3ba9e] text-[#0d0d0e] border-[#c3ba9e]"
                        : "border-[#2a2a2d] text-[#9a9787] hover:border-[#c3ba9e] hover:text-[#c3ba9e]"
                    }`}
                    data-testid={`order-status-btn-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Items */}
            <div className="bg-[#161617] border border-[#2a2a2d]">
              <p className="px-6 pt-5 text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">Items ({order.items.length})</p>
              <div className="divide-y divide-[#2a2a2d] mt-3">
                {order.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-4 px-6 py-4">
                    <img src={it.image} alt={it.name} className="w-14 h-16 object-cover bg-[#0d0d0e]" />
                    <div className="flex-1">
                      <p className="text-[#e9e6db] text-sm">{it.name}</p>
                      <p className="text-[11px] text-[#5e5d54] mt-0.5">{it.color} · Size {it.size} · ×{it.qty}</p>
                    </div>
                    <p className="text-[#e9e6db] text-sm">{fmt(it.price * it.qty)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#2a2a2d] p-6 space-y-2 text-sm">
                <Row label="Subtotal" value={fmt(order.totals.subtotal)} />
                {order.totals.discount_amount > 0 && (
                  <Row label={`Discount (${order.totals.discount_percent}%)`} value={`−${fmt(order.totals.discount_amount)}`} />
                )}
                <Row label="Shipping" value={order.totals.shipping_fee ? fmt(order.totals.shipping_fee) : "Complimentary"} />
                <div className="border-t border-[#2a2a2d] pt-3 flex justify-between">
                  <span className="text-[#e9e6db]">Total</span>
                  <span className="text-[#e9e6db] font-serif-display text-2xl">{fmt(order.totals.total)}</span>
                </div>
              </div>
            </div>

            {/* Shipping / Tracking */}
            <div className="bg-[#161617] border border-[#2a2a2d] p-6 space-y-4">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">Shipping & tracking</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <AdminField label="Tracking number">
                  <TrackingInput value={order.tracking_number || ""} onSave={(v) => patch({ tracking_number: v || null })} />
                </AdminField>
                <AdminField label="Carrier">
                  <TrackingInput value={order.carrier || ""} onSave={(v) => patch({ carrier: v || null })} placeholder="Delhivery, BlueDart…" />
                </AdminField>
              </div>
              <AdminField label="Admin note">
                <TrackingInput value={order.admin_note || ""} onSave={(v) => patch({ admin_note: v || "" })} textarea />
              </AdminField>
            </div>
          </div>

          {/* Right summary */}
          <aside className="space-y-6">
            <div className="bg-[#161617] border border-[#2a2a2d] p-5">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">Customer</p>
              <p className="font-serif-display text-2xl text-[#e9e6db] mt-2">{order.shipping.full_name}</p>
              <p className="text-sm text-[#9a9787] mt-1">{order.shipping.email}</p>
              <p className="text-sm text-[#9a9787]">{order.shipping.phone}</p>
              <p className="text-[11px] text-[#5e5d54] mt-3">
                {order.customer_id ? "Registered customer" : "Guest checkout"}
              </p>
            </div>

            <div className="bg-[#161617] border border-[#2a2a2d] p-5">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">Address</p>
              <p className="text-sm text-[#e9e6db] mt-2 leading-relaxed">
                {order.shipping.address_line1}
                {order.shipping.address_line2 && <><br />{order.shipping.address_line2}</>}
                <br />{order.shipping.city}, {order.shipping.state} {order.shipping.pincode}
                <br />{order.shipping.country}
              </p>
            </div>

            <div className="bg-[#161617] border border-[#2a2a2d] p-5">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">Payment</p>
              <p className="text-sm text-[#e9e6db] mt-2 uppercase tracking-wider">{order.payment_method}</p>
              <p className="text-[11px] text-[#9a9787] mt-2">Status</p>
              <select
                value={order.payment_status}
                onChange={(e) => patch({ payment_status: e.target.value })}
                className="mt-1 w-full bg-[#0d0d0e] border border-[#2a2a2d] px-3 py-2 text-[13px] text-[#e9e6db] focus:border-[#c3ba9e] outline-none"
                data-testid="order-payment-status"
              >
                {["pending", "paid", "failed", "refunded"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>

            <button
              onClick={() => printInvoice(order)}
              className="w-full border border-[#2a2a2d] text-[#9a9787] px-5 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#1f1f21] flex items-center justify-center gap-2"
              data-testid="order-print"
            >
              <Printer size={14} /> Print invoice
            </button>

            {/* Fulfillment actions */}
            <div className="bg-[#161617] border border-[#2a2a2d] p-5 space-y-3">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">Shipping & fulfillment</p>
              {!order.shiprocket_shipment_id ? (
                <button
                  onClick={ship}
                  disabled={saving}
                  className="w-full bg-[#c3ba9e] text-[#0d0d0e] px-5 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#FDFAF6] disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="admin-order-ship"
                >
                  <Send size={13} /> Ship via Shiprocket
                </button>
              ) : (
                <div className="text-[12px] text-[#9a9787] space-y-1">
                  <p>AWB: <span className="text-[#e9e6db]">{order.tracking_number || "—"}</span></p>
                  <p>Carrier: <span className="text-[#e9e6db]">{order.carrier || "—"}</span></p>
                  <p>Last SR status: <span className="text-[#e9e6db]">{order.shiprocket_last_status || "—"}</span></p>
                  {(order.shiprocket_label_url || order.shiprocket_manifest_url) && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {order.shiprocket_label_url && (
                        <a href={order.shiprocket_label_url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase border border-[#2a2a2d] text-[#c3ba9e] hover:bg-[#1f1f21]" data-testid="admin-order-label">Label PDF</a>
                      )}
                      {order.shiprocket_manifest_url && (
                        <a href={order.shiprocket_manifest_url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase border border-[#2a2a2d] text-[#c3ba9e] hover:bg-[#1f1f21]" data-testid="admin-order-manifest">Manifest PDF</a>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 pt-3">
                    <button onClick={refreshTracking} disabled={saving} className="border border-[#2a2a2d] text-[#9a9787] px-3 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#1f1f21] disabled:opacity-60 flex items-center justify-center gap-1" data-testid="admin-order-track">
                      <RefreshCcw size={11} /> Sync tracking
                    </button>
                    <button onClick={cancelShipment} disabled={saving} className="border border-[#2a2a2d] text-[#c97070] px-3 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#1f1f21] disabled:opacity-60 flex items-center justify-center gap-1" data-testid="admin-order-cancel-shipment">
                      <Ban size={11} /> Cancel
                    </button>
                  </div>
                  <button onClick={createReturn} disabled={saving} className="w-full mt-2 border border-[#2a2a2d] text-[#c3ba9e] px-3 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#1f1f21] disabled:opacity-60 flex items-center justify-center gap-1" data-testid="admin-order-return">
                    <Undo2 size={11} /> Create return pickup
                  </button>
                </div>
              )}
            </div>

            {/* Razorpay refund */}
            {order.payment_method === "razorpay" && order.razorpay_payment_id && order.payment_status === "paid" && (
              <button onClick={refund} disabled={saving} className="w-full border border-[#c97070]/40 text-[#e69a9a] px-5 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#1f1f21] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="admin-order-refund">
                <IndianRupee size={13} /> Refund via Razorpay
              </button>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-[#9a9787]">
      <span>{label}</span>
      <span className="text-[#e9e6db]">{value}</span>
    </div>
  );
}

function AdminField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e] mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function TrackingInput({ value, onSave, textarea, placeholder }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const commit = () => v !== value && onSave(v);
  return textarea ? (
    <textarea
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      rows={3}
      placeholder={placeholder}
      className="w-full bg-[#0d0d0e] border border-[#2a2a2d] px-3 py-2.5 text-[13px] text-[#e9e6db] focus:border-[#c3ba9e] outline-none resize-none"
    />
  ) : (
    <input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      placeholder={placeholder}
      className="w-full bg-[#0d0d0e] border border-[#2a2a2d] px-3 py-2.5 text-[13px] text-[#e9e6db] focus:border-[#c3ba9e] outline-none"
    />
  );
}
